import React, { useState, useRef } from 'react';
import { useNotificationStore } from '@/stores/notificationStore';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { StatCard } from '@/components/ui/StatCard';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent,
  CardFooter,
  Button,
  Alert,
  Progress,
  Badge,
  Input
} from '@/components/ui';
import { Download } from 'lucide-react';
import { AirportsService, type Airport, type ImportResult, type ValidationResult, type AirportStats } from '@/services/AirportsService';

export function AirportsManagement() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [airportStats, setAirportStats] = useState<AirportStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Airport[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { push: addNotification } = useNotificationStore();
  const airportsService = new AirportsService();

  // Load airport statistics on component mount
  React.useEffect(() => {
    loadAirportStats();
  }, []);

  const loadAirportStats = async () => {
    setIsLoadingStats(true);
    try {
      const stats = await airportsService.getStats();
      setAirportStats(stats);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Failed to load statistics',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        addNotification({
          type: 'error',
          title: 'Invalid file type',
          message: 'Please select a CSV file'
        });
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        addNotification({
          type: 'error',
          title: 'File too large',
          message: 'File size must be less than 50MB'
        });
        return;
      }
      
      setSelectedFile(file);
      setValidationResult(null);
      setImportResult(null);
    }
  };

  const validateCSV = async () => {
    if (!selectedFile) return;
    
    setIsValidating(true);
    try {
      const result = await airportsService.validateCSV(selectedFile);
      setValidationResult(result);
      addNotification({
        type: 'success',
        title: 'File validated',
        message: `Found ${result.mediumLargeAirports} medium/large airports out of ${result.totalRows} total rows`
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Validation failed',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsValidating(false);
    }
  };

  const importCSV = async () => {
    if (!selectedFile) return;
    
    setIsImporting(true);
    try {
      const result = await airportsService.importCSV(selectedFile);
      setImportResult(result);
      addNotification({
        type: 'success',
        title: 'Import completed',
        message: `Processed ${result.processed} rows, inserted ${result.inserted}, updated ${result.updated}`
      });
      // Refresh statistics
      await loadAirportStats();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Import failed',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const searchAirportsByCity = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const airports = await airportsService.searchByCity(query.trim(), 5);
      setSearchResults(airports);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Search failed',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search function
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAirportsByCity(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const clearSelection = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    try {
      addNotification({
        type: 'info',
        title: 'Export Started',
        message: 'Preparing your CSV export...'
      });

      // Export all airports (no filters applied)
      const params = new URLSearchParams();

      const response = await airportsService.exportCSV(params);
      
      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `airports-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addNotification({
        type: 'success',
        title: 'Export Complete',
        message: 'Your CSV export has been downloaded'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export airports'
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Airports Management"
        description="Import and manage airport data for flight cost estimation"
      >
        <Button
          onClick={handleExport}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </PageHeader>

      <div className="space-y-6">
        {/* Current Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Current Airport Database</CardTitle>
          <CardDescription>
            Overview of airports currently in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingStats ? (
            <div className="text-center py-8">Loading statistics...</div>
          ) : airportStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Airports"
                value={airportStats.total}
                iconColor="primary"
              />
              <StatCard
                label="With IATA Codes"
                value={airportStats.with_iata}
                iconColor="primary"
              />
              <StatCard
                label="Scheduled Service"
                value={airportStats.with_scheduled_service}
                iconColor="primary"
              />
              <StatCard
                label="Medium/Large"
                value={(airportStats.by_type.medium_airport || 0) + (airportStats.by_type.large_airport || 0)}
                iconColor="primary"
              />
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load statistics</div>
          )}
        </CardContent>
      </Card>

      {/* Airport Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Airports by City</CardTitle>
          <CardDescription>
            Enter a city name to find the nearest airports. Search includes airport names and locations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Input
              type="text"
              placeholder="Enter city name (e.g., Los Angeles, London, Tokyo)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {isSearching && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>

          {searchQuery.length >= 2 && (
            <div>
              {searchResults.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Found {searchResults.length} airports:</h4>
                  <div className="space-y-3">
                    {searchResults.map((airport) => (
                      <div
                        key={airport.iata}
                        className="p-3 bg-gray-50 rounded-lg border"
                      >
                        {/* Mobile-first: stack vertically, then horizontal on larger screens */}
                        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                          {/* Left section: IATA + Name + Details */}
                          <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                                <span className="text-primary-600 font-semibold text-xs">{airport.iata}</span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 leading-tight">
                                {airport.name}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                  {airport.iso_country}
                                </span>
                                <Badge 
                                  variant={airport.type === 'large_airport' ? 'default' : 'secondary'} 
                                  className="text-xs py-0.5"
                                >
                                  {airport.type.replace('_', ' ')}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          {/* Right section: Coordinates - full width on mobile */}
                          <div className="flex items-center justify-start sm:justify-end text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded sm:bg-transparent sm:px-0 sm:py-0 sm:ml-4">
                            <div className="flex items-center space-x-1">
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0z" />
                              </svg>
                              <span className="font-mono">{airport.lat.toFixed(3)}, {airport.lon.toFixed(3)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                !isSearching && (
                  <div className="text-center py-6 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p>No airports found for "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 mt-1">Try searching for a major city or airport name</p>
                  </div>
                )
              )}
            </div>
          )}

          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <div className="text-sm text-gray-500">
              <p>Type at least 2 characters to search</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Import Airports CSV</CardTitle>
          <CardDescription>
            Upload a CSV file from OurAirports.com. Only medium and large airports will be imported.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="mb-4">
                <label htmlFor="csv-file" className="cursor-pointer">
                  <span className="text-primary-600 hover:text-primary-500 font-medium">
                    Click to select a CSV file
                  </span>
                  <span className="text-gray-500"> or drag and drop</span>
                </label>
                <input
                  ref={fileInputRef}
                  id="csv-file"
                  type="file"
                  accept=".csv,text/csv,application/csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-gray-500">CSV files up to 50MB</p>
            </div>
          </div>

          {selectedFile && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-900">{selectedFile.name}</p>
                  <p className="text-sm text-blue-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Remove
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        
        {selectedFile && (
          <CardFooter className="space-x-3">
            <Button
              onClick={validateCSV}
              disabled={isValidating}
              variant="outline"
            >
              {isValidating ? 'Validating...' : 'Validate CSV'}
            </Button>
            
            {validationResult && validationResult.isValid && (
              <Button
                onClick={importCSV}
                disabled={isImporting}
              >
                {isImporting ? 'Importing...' : 'Import Airports'}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      {/* Validation Results */}
      {validationResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>Validation Results</span>
              <Badge variant={validationResult.isValid ? 'success' : 'error'}>
                {validationResult.isValid ? 'Valid' : 'Invalid'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold">{validationResult.totalRows}</div>
                <div className="text-xs md:text-sm text-gray-600">Total Rows</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold">{validationResult.validRows}</div>
                <div className="text-xs md:text-sm text-gray-600">Valid Rows</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold text-primary-600">{validationResult.mediumLargeAirports}</div>
                <div className="text-xs md:text-sm text-gray-600">Medium/Large</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold text-red-600">{validationResult.errors.length}</div>
                <div className="text-xs md:text-sm text-gray-600">Errors</div>
              </div>
            </div>

            {validationResult.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">First 10 Errors:</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-1 text-sm">
                    {validationResult.errors.slice(0, 10).map((error, index) => (
                      <li key={index} className="text-red-700">
                        Row {error.row}: {error.field ? `${error.field} - ` : ''}{error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>Import Results</span>
              <Badge variant={importResult.success ? 'success' : 'warning'}>
                {importResult.success ? 'Success' : 'Completed with Errors'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold">{importResult.processed}</div>
                <div className="text-xs md:text-sm text-gray-600">Processed</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold text-green-600">{importResult.inserted}</div>
                <div className="text-xs md:text-sm text-gray-600">Inserted</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold text-blue-600">{importResult.updated}</div>
                <div className="text-xs md:text-sm text-gray-600">Updated</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-lg md:text-xl font-semibold text-gray-600">{importResult.skipped}</div>
                <div className="text-xs md:text-sm text-gray-600">Skipped</div>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Errors ({importResult.errors.length}):</h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-1 text-sm">
                    {importResult.errors.slice(0, 10).map((error, index) => (
                      <li key={index} className="text-yellow-700">
                        Row {error.row}: {error.field ? `${error.field} - ` : ''}{error.message}
                      </li>
                    ))}
                  </ul>
                  {importResult.errors.length > 10 && (
                    <p className="text-yellow-600 text-xs mt-2">
                      ... and {importResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Import Statistics */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Import Breakdown:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">By Airport Type:</h5>
                  <ul className="text-sm space-y-1">
                    {Object.entries(importResult.stats.by_type).map(([type, count]) => (
                      <li key={type} className="flex justify-between">
                        <span className="capitalize">{type.replace('_', ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">By Country (Top 10):</h5>
                  <ul className="text-sm space-y-1">
                    {Object.entries(importResult.stats.by_country)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([country, count]) => (
                        <li key={country} className="flex justify-between">
                          <span>{country}</span>
                          <span className="font-medium">{count}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm text-gray-600">
            <ol className="space-y-2">
              <li>Download the airports.csv file from <a href="https://ourairports.com/data/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">OurAirports.com</a></li>
              <li>Select the CSV file using the upload area above</li>
              <li>Click "Validate CSV" to check the file format and content</li>
              <li>If validation passes, click "Import Airports" to add the data to the database</li>
              <li>Only medium_airport and large_airport types will be imported</li>
              <li>Existing airports will be updated with new data</li>
            </ol>
            
            <Alert className="mt-4">
              <div className="font-medium">Note:</div>
              <div>The import process filters airports to include only medium and large airports with valid IATA or ICAO codes. Small airports, heliports, and other types are automatically skipped.</div>
            </Alert>
          </div>
        </CardContent>
      </Card>
      </div>
    </PageContainer>
  );
}